/** Окно параметров фиксации: квартал (расчёт на 1-е число квартала) или новинки (расчёт по
 *  умолчанию на сегодня); «действует с» = дата расчёта, пока её не поменяли руками; тираж 3000. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";

import { createFixation, type FixationKind } from "@/api/purchasePrices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { dateRu, quarterOptions, quarterStart, todayIso } from "./format";
import { KEY } from "./queries";
import { ErrorBox, Field, Modal, selectCls } from "./ui";

export const DEFAULT_QTY = 3000;

export function FixationStartDialog({
  kind,
  onClose,
  onStarted,
}: {
  kind: FixationKind;
  onClose: () => void;
  onStarted: (id: number) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const today = todayIso();
  const initialCalc = kind === "quarter" ? quarterStart(today) : today;
  const [calcDate, setCalcDate] = useState(initialCalc);
  const [validFrom, setValidFrom] = useState(initialCalc);
  const [validTouched, setValidTouched] = useState(false);
  const [qty, setQty] = useState(String(DEFAULT_QTY));
  const [tried, setTried] = useState(false);
  const qtyN = /^\d+$/.test(qty.trim()) ? Number(qty.trim()) : NaN;
  const qtyErr = Number.isInteger(qtyN) && qtyN >= 1 && qtyN <= 1_000_000 ? null : "целое 1…1 000 000";

  const changeCalc = (v: string): void => {
    setCalcDate(v);
    if (!validTouched) setValidFrom(v);
  };

  const m = useMutation({
    mutationFn: (body: Parameters<typeof createFixation>[0]) => createFixation(body),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: KEY.fixations });
      onStarted(r.id);
    },
    // 502 — фиксация создана и сразу failed: в списке она должна появиться
    onError: () => void qc.invalidateQueries({ queryKey: KEY.fixations }),
  });
  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setTried(true);
    if (!calcDate || !validFrom || qtyErr) return;
    m.mutate({ kind, calc_date: calcDate, valid_from: validFrom, qty: qtyN });
  };

  return (
    <Modal title={kind === "quarter" ? "Зафиксировать квартал" : "Догрузить новинки"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="fixation-form">
        <p className="text-xs text-muted-foreground">
          {kind === "quarter"
            ? "Портал производства посчитает себестоимость всего каталога на 1-е число квартала. Ручные и контрактные цены фиксация не трогает."
            : "Портал производства посчитает весь каталог; в предпросмотр попадут только характеристики без действующей цены."}{" "}
          Расчёт идёт несколько минут; до подтверждения ничего не записывается.
        </p>
        {kind === "quarter" ? (
          <Field label="Квартал (дата расчёта — 1-е число)">
            {(id) => (
              <select id={id} className={selectCls} value={calcDate} onChange={(e) => changeCalc(e.target.value)}>
                {quarterOptions(today).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </Field>
        ) : (
          <Field label="Дата расчёта" error={tried && !calcDate ? "укажите дату" : null}>
            {(id) => <Input id={id} type="date" value={calcDate} onChange={(e) => changeCalc(e.target.value)} />}
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Цены действуют с"
            error={tried && !validFrom ? "укажите дату" : null}
            hint={validFrom !== calcDate ? `расчёт на ${dateRu(calcDate)}, действует с ${dateRu(validFrom)}` : null}
          >
            {(id) => (
              <Input
                id={id}
                type="date"
                value={validFrom}
                onChange={(e) => {
                  setValidTouched(true);
                  setValidFrom(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label="Тираж, шт" error={tried ? qtyErr : null}>
            {(id) => <Input id={id} inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} />}
          </Field>
        </div>
        {m.isError && <ErrorBox error={m.error} what="Фиксация не запущена" />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={m.isPending}>
            Запустить расчёт
          </Button>
        </div>
      </form>
    </Modal>
  );
}
